class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';

    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    // Mejorar la detección de la marca actual
    let vendor = this.getCurrentVendor();
    
    // Nunca usamos caché para búsquedas con vendor específico
    // Esto asegura que siempre obtengamos resultados frescos
    if (this.cachedResults[queryKey] && !vendor) {
      this.renderSearchResults(this.cachedResults[queryKey], queryKey);
      return;
    }
    
    // Configurar los parámetros para la API de Shopify según la documentación oficial
    // https://shopify.dev/docs/api/ajax/reference/predictive-search
    
    // Parámetros base
    let searchParams = new URLSearchParams();
    searchParams.append('q', searchTerm);
    
    // Tipos de recursos a buscar
    searchParams.append('resources[type]', 'product');
    
    // Límites para cada tipo de recurso
    searchParams.append('resources[product][limit]', '8'); // Aumentamos el límite para tener más resultados para filtrar
    //searchParams.append('resources[page][limit]', '3');
    //searchParams.append('resources[article][limit]', '3'); //comentamos collection, page y articles de la predictive y cambiamos limite a 8 
    //searchParams.append('resources[collection][limit]', '3');
    
    // Establecer campos de búsqueda específicos para productos, priorizando el vendor
    searchParams.append('resources[options][fields]', 'title,product_type,variants.title,vendor,variants.sku');
    
    // Filtro de vendor - Nota: este no está en la documentación oficial pero estamos probando
    if (vendor) {
      searchParams.append('resources[product][filters]', `vendor:${vendor}`);
      
      // También añadimos el vendor a la consulta para aumentar las probabilidades de encontrar productos relevantes
      if (!searchTerm.toLowerCase().includes(vendor.toLowerCase())) {
        searchParams.append('q', searchTerm + ' ' + vendor);
      }
    }
    
    // Sección ID para la renderización HTML
    searchParams.append('section_id', 'predictive-search');
    
    // Construir la URL completa
    const searchUrl = `${window.Shopify.routes.predictive_search_url}?${searchParams.toString()}`;

    fetch(searchUrl, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const parser = new DOMParser();
        const parsedDocument = parser.parseFromString(text, 'text/html');
        const resultsContainer = parsedDocument.querySelector('#shopify-section-predictive-search');
        
        if (!resultsContainer) {
          return;
        }
        
        const resultsMarkup = resultsContainer.innerHTML;
        // Solo guardamos en caché si no es una búsqueda específica por vendor
        if (!vendor) {
          // Save bandwidth keeping the cache in all instances synced
          this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
            predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
          });
        }
        this.renderSearchResults(resultsMarkup, queryKey);
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup, queryKey_) {
    // Verifica si hay contenido real en los resultados
    if (!resultsMarkup || resultsMarkup.trim() === '') {
      return;
    }
    
    // Obtener el vendor actual
    const currentVendor = this.getCurrentVendor();
    
    // Crear un elemento temporal para manipular el HTML
    const tempElement = document.createElement('div');
    tempElement.innerHTML = resultsMarkup;
    
    // Si tenemos un vendor específico, filtrar los resultados
    if (currentVendor && currentVendor !== 'Limonada') {
      // Encontrar todos los elementos de producto
      const productItems = tempElement.querySelectorAll('.predictive-search__list-item');
      let productosVisibles = 0;
      
      // Recorrer todos los elementos y ocultar los que no coincidan con el vendor
      productItems.forEach(item => {
        const vendorElement = item.querySelector('.predictive-search__item-vendor');
        
        // Si encontramos el elemento vendor, verificar su contenido
        if (vendorElement) {
          const vendorText = vendorElement.textContent.trim();
          
          // Si el vendor no coincide, ocultar el elemento
          if (vendorText !== currentVendor) {
            item.style.display = 'none';
          } else {
            productosVisibles++;
          }
        }
      });
      
      // Si no hay productos visibles, mostrar un mensaje
      if (productosVisibles === 0) {
        const productGroup = tempElement.querySelector('.predictive-search__result-group');
        if (productGroup) {
          const noResultsMessage = document.createElement('p');
          noResultsMessage.className = 'predictive-search__no-results';
          noResultsMessage.textContent = `No se encontraron productos de ${currentVendor} para esta búsqueda.`;
          productGroup.appendChild(noResultsMessage);
        }
      }
    }

    // Resaltar palabra encontrada
    // Encontrar todos los elementos de producto
    const productItems_ = tempElement.querySelectorAll('.predictive-search__list-item');
    
    // Recorrer todos los elementos y ocultar los que no coincidan con el vendor
    productItems_.forEach(item => {
      const vendorElement_ = item.querySelector('.predictive-search__item-heading');
      
      // Si encontramos el elemento vendor, verificar su contenido
      if (vendorElement_) {
        const vendorText_ = vendorElement_.textContent.trim();
        if (incluyeTexto(queryKey_, vendorText_)) {
          vendorElement_.innerHTML = resaltarTexto(queryKey_, vendorText_);
        }
        // console.log("Subrayar:", queryKey_, vendorText_);
      }
    });

  
    function resaltarTexto(textoBuscado, contenidoHTML) {
      const regex = new RegExp(`(${textoBuscado})`, 'ig');
      return contenidoHTML.replace(regex, '<b>$1</b>');
    }
  
    function incluyeTexto(textoBuscado, textoCompleto) {
      return textoCompleto.toLowerCase().includes(textoBuscado.toLowerCase());
    }
    
    this.predictiveSearchResults.innerHTML = tempElement.innerHTML;
    this.setAttribute('results', true);
    this.setAttribute('open', true);

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    
    try {
      const liveRegionElement = this.querySelector('[data-predictive-search-live-region-count-value]');
      
      if (liveRegionElement) {
        const textContent = liveRegionElement.textContent;
        this.setLiveRegionText(textContent);
      } else {
        this.setLiveRegionText('Resultados de búsqueda disponibles');
      }
    } catch (error) {
      this.setLiveRegionText('Resultados de búsqueda disponibles');
    }
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    const maxHeight = this.resultsMaxHeight || this.getResultsMaxHeight();
    
    try {
      this.predictiveSearchResults.style.maxHeight = `${maxHeight}px`;
      this.setAttribute('open', true);
      this.input.setAttribute('aria-expanded', true);
      this.isOpen = true;
    } catch (error) {
      // Silent error handling
    }
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }

  // Función auxiliar para detectar el vendor actual con mayor precisión
  getCurrentVendor() {
    // Buscar en la URL primero (prioridad más alta)
    const urlParams = new URLSearchParams(window.location.search);
    const vendorParam = urlParams.get('filter.p.vendor');
    if (vendorParam) {
      return decodeURIComponent(vendorParam);
    }
    
    // Verificar la URL actual y clases del body
    const urlPath = window.location.pathname;
    const bodyClasses = document.body.classList;
    
    // Verificar clases específicas del body
    if (bodyClasses.contains('limonada-4kids')) {
      return '4 Kids';
    } else if (bodyClasses.contains('limonada-blue')) {
      return 'Black and Blue';
    }
    
    // Verificar patrones en la URL
    if (urlPath.includes('limonada-4kids') || urlPath.includes('total-look-4kids')) {
      return '4 Kids';
    } else if (urlPath.includes('limonada-blue') || urlPath.includes('total-look-blackandblue')) {
      return 'Black and Blue';
    }
    
    // Verificar otros parámetros en la URL
    const tabParam = urlParams.get('tab');
    if (tabParam) {
      if (tabParam.includes('4+Kids')) {
        return '4 Kids';
      } else if (tabParam.includes('Black+and+Blue')) {
        return 'Black and Blue';
      }
    }
    
    // Buscar meta tags específicos para páginas de producto
    if (bodyClasses.contains('template-product')) {
      const productVendorMeta = document.querySelector('meta[name="product:vendor"]');
      if (productVendorMeta) {
        return productVendorMeta.getAttribute('content');
      }
    }
    
    // Revisar la presencia de elementos específicos por marca
    const limonadaBlueHeader = document.querySelector('#shopify-section-limonada-blue-header');
    if (limonadaBlueHeader) {
      return 'Black and Blue';
    }
    
    const limonada4KidsHeader = document.querySelector('#shopify-section-limonada-4kids-header');
    if (limonada4KidsHeader) {
      return '4 Kids';
    }
    
    // Por defecto, devolver Limonada
    return 'Limonada';
  }
}

// Define selectCallback if it doesn't exist to prevent errors in search page
if (typeof window.selectCallback === 'undefined') {
  window.selectCallback = function(variant, selector) {
    // Implementación básica para evitar errores
    if (variant && variant.available) {
      // Habilitar el botón de añadir al carrito si la variante está disponible
      const addToCartButtons = document.querySelectorAll('.add-to-cart');
      addToCartButtons.forEach(button => {
        button.disabled = false;
        button.classList.remove('disabled');
        button.textContent = window.variantStrings?.addToCart || 'Agregar al carrito';
      });
    } else {
      // Deshabilitar el botón si la variante no está disponible
      const addToCartButtons = document.querySelectorAll('.add-to-cart');
      addToCartButtons.forEach(button => {
        button.disabled = true;
        button.classList.add('disabled');
        button.textContent = window.variantStrings?.soldOut || 'Sin stock';
      });
    }
  };
}

customElements.define('predictive-search', PredictiveSearch);
